import React, { useEffect, useState } from "react";
import {
  WizardFormField,
  DEFAULT_COLOR,
} from "../machines/immersive-wizard.machine";
import { WizardFormData } from "../types/wizard.types";
import { ColorPicker } from "@pushpress/pushpress-ui";

interface ColorStepProps {
  formData: WizardFormData;
  onFormUpdate: (data: Partial<WizardFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

const ColorStep: React.FC<ColorStepProps> = ({
  formData,
  onFormUpdate,
  onValidationChange,
}) => {
  const colorOptions = [
    { id: DEFAULT_COLOR, name: "Blue" },
    { id: "#4F46E5", name: "Indigo" },
    { id: "#06B6D4", name: "Teal" },
    { id: "#10B981", name: "Green" },
    { id: "#F59E0B", name: "Yellow" },
    { id: "#F97316", name: "Orange" },
    { id: "#EF4444", name: "Red" },
    { id: "#EC4899", name: "Pink" },
    { id: "#8B5CF6", name: "Purple" },
  ];

  const predefinedColorIds = colorOptions.map((color) => color.id);
  const currentColor = formData[WizardFormField.Color] || DEFAULT_COLOR;
  const isCurrentColorPredefined = predefinedColorIds.includes(currentColor);

  const [customColor, setCustomColor] = useState<string>(() => {
    return isCurrentColorPredefined ? "" : currentColor;
  });

  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  // Update custom color state when form data changes
  useEffect(() => {
    if (!isCurrentColorPredefined && currentColor !== customColor) {
      setCustomColor(currentColor);
    }
  }, [currentColor, isCurrentColorPredefined, customColor]);

  const handleColorChange = (color: string) => {
    onFormUpdate({ [WizardFormField.Color]: color });
  };

  const handleCustomColorChange = (color: string) => {
    setCustomColor(color);
    handleColorChange(color);
  };

  const isCustomColorSelected =
    !isCurrentColorPredefined && currentColor === customColor;

  return (
    <fieldset>
      <div className="tw-mt-6 tw-grid tw-grid-cols-5 tw-gap-x-3 tw-gap-y-3 tw-justify-items-center">
        {colorOptions.map((color) => {
          const isChecked = formData[WizardFormField.Color] === color.id;
          return (
            <div
              key={color.id}
              className="tw-flex tw-rounded-full tw-outline -tw-outline-offset-1 tw-outline-black/0"
            >
              <input
                value={color.id}
                checked={isChecked}
                onChange={(e) => handleColorChange(e.target.value)}
                name="brandColor"
                type="radio"
                aria-label={color.name}
                className={`tw-size-10 tw-appearance-none tw-rounded-full tw-forced-color-adjust-none tw-transition-all tw-duration-200 ${
                  isChecked ? "tw-outline tw-outline-2 tw-outline-offset-2" : ""
                } focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-offset-[3px]`}
                style={{
                  backgroundColor: color.id,
                  outlineColor: isChecked ? color.id : undefined,
                }}
              />
            </div>
          );
        })}

        {/* Custom Color Picker */}
        <div
          className={`tw-flex tw-rounded-full tw-outline -tw-outline-offset-1 tw-outline-black/0 tw-transition-all tw-duration-200 ${
            isCustomColorSelected
              ? "tw-outline tw-outline-2 tw-outline-offset-2"
              : ""
          }`}
          style={
            isCustomColorSelected ? { outlineColor: customColor } : undefined
          }
        >
          <ColorPicker
            value={customColor || "#FFFFFF"}
            onChange={handleCustomColorChange}
            circular={true}
            size="tw-size-10"
            className="focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-offset-[3px]"
            name="customBrandColor"
          />
        </div>
      </div>
    </fieldset>
  );
};

export default ColorStep;
